import cn from "classnames";
import dayjs from "dayjs";
import React, { useCallback, useMemo } from "react";

import AutoAwesomeRoundedIcon from "@mui/icons-material/AutoAwesomeRounded";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Grid,
  IconButton,
  Tooltip,
  Typography,
  useMediaQuery,
} from "@mui/material";
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import { PROGRAM_TEXT } from "@/constants/airport";
import { airlineBgColor } from "@/constants/config";
import usePlatform from "@/hooks/usePlatform";
import useThemeData from "@/hooks/useThemeData";
import { FlightItem as FlightItemType } from "@/typings";
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';

import ExpandBtn from "./ExpandBtn";
import InfoBtn from "./InfoBtn";
import ProductInfo from "./ProductInfo";
import SummaryAirline from "./SummaryAirline";
import SummaryInfo from "./SummaryInfo";
import SummaryOptions from "./SummaryOptions";

interface FlightItemProps {
  data: FlightItemType;
  isExpanded: boolean;
  onExpandChange: (key: string) => void;
  onOpenOptionDialog: (data: FlightItemType) => void;
  onOpenRouteDialog?: (data: FlightItemType) => void;
  onSearch?: (data: FlightItemType) => void;
  showLastSeen?: boolean;
  showSearchBtn?: boolean;
  needRouteBtn?: boolean;
  partialDisplay?: boolean;
}

const FlightItem = ({
  isExpanded,
  data,
  onExpandChange,
  onOpenOptionDialog,
  onOpenRouteDialog,
  showLastSeen = false,
  showSearchBtn,
  onSearch,
  needRouteBtn,
  partialDisplay = false,
}: FlightItemProps) => {
  const { isMobile } = usePlatform();
  const theme = useThemeData();

  const products = useMemo(() => data?.fare?.products || [], [data]);

  const handleExpandChange = useCallback(() => {
    onExpandChange(data.hash);
  }, [data, onExpandChange]);

  const handleOpenOptionDialog = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      onOpenOptionDialog(data);
    },
    [data, onOpenOptionDialog]
  );
  const handleOpenRouteDialog = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      onOpenRouteDialog?.(data);
    },
    [data, onOpenRouteDialog]
  );

  const isMediaMax900 = useMediaQuery("(max-width:1000px)");
  const isMediaMin800 = useMediaQuery("(min-width:900px)");
  const last_seen_time = data.l_s || data.last_seen;
  const alertDetailPage = !(window.location.pathname === "/flight");
  
  
  return (
    <Accordion
      expanded={isExpanded}
      onChange={handleExpandChange}
      TransitionProps={{ unmountOnExit: true }}
      sx={{
        boxShadow: "2",
        borderRadius: isExpanded ? "14px" : "0px",
        border: (theme) => `0px solid ${theme.palette.divider}`,
      }}
    >
      <AccordionSummary
        id={`${data.hash}`}
        key={`${data.hash}`}
        className={cn(isMobile ? "!px-[10px]" : "!px-[20px]", "relative")}
        onClick={handleExpandChange}
      >

        {!isMobile && (
          <Box
            className="max-[2270px] absolute top-[6px] left-[0px] px-[10px] text-[12px] textColor-[black] rounded-full flex items-center"
            sx={{ maxWidth: "2000px" }}
          >
            <Tooltip title="Last seen time" placement="top">
              <div className="flex items-center mr-[5px] rounded-md px-2 py-0"
                style={{ borderWidth: '2px', borderStyle: 'solid', borderColor: theme.isDarkMode ? "#424242" : "#f5f5f5" }}>
                {/* <AccessTimeIcon fontSize="small" className="mr-[3px] !text-[14px]" /> */}
                {dayjs(last_seen_time * 1000).fromNow() == "Real-time" || dayjs(last_seen_time * 1000).fromNow() == "just now" || !alertDetailPage ?
                  <span className="relative mx-[0px] mr-[10px] flex h-[6px] w-[6px]">
                    <span className="absolute -left-[3px] -top-[3px] inline-flex h-[12px] w-[12px] animate-[ping_2s_ease-in-out_infinite] rounded-full opacity-75" style={{ backgroundColor: theme.palette.primary.main }}>
                    </span>
                    <span className={theme.isDarkMode ? "relative inline-flex h-[6px] w-[6px] rounded-full ring-[1px] ring-offset-[2px]" : "relative inline-flex h-[6px] w-[6px] rounded-full bg-[#1976d2] ring-[1px] ring-[#1976d2] ring-offset-[2px]"}
                      style={{
                        backgroundColor: theme.palette.primary.main,
                        // Removed the problematic CSS variable
                      }}>
                    </span>
                  </span> : <AccessTimeIcon fontSize="small" className="mr-[3px] !text-[14px]" />}
                <Typography fontSize={12} className="mr-[3px]">{alertDetailPage ? dayjs(last_seen_time * 1000).fromNow() : "Real-time"}</Typography>
              </div>
            </Tooltip>
            {PROGRAM_TEXT[data.program_code] && <Tooltip
              title={PROGRAM_TEXT[data.program_code]?.join(" ")}
              placement="top"
            >
              <IconButton
                size="small"
                sx={{
                  // backgroundColor: theme.isDarkMode ? "#424242" : "#f5f5f5",
                  padding: "4px",
                }}
              >
                <CheckCircleOutlineIcon fontSize="small" className="!text-[14px]" />
              </IconButton>
            </Tooltip>}
            {data?.is_new && <span className="text-[12px] text-white rounded-full px-3 py-0 font-bold flex items-center ml-[8px]" style={{ backgroundColor: theme.palette.primary.main }}>
              <span className="mr-1 inline-block h-2 w-2 rounded-full bg-white"></span>
              Newfound Flight
            </span>}
          </Box>
        )}

        <div className="flex items-center w-full pt-[6px]">
          <Grid container className="w-full" rowSpacing={1} alignItems="center">
            <Grid item xs={12} sm={4}>
              <SummaryInfo
                showLastSeen={showLastSeen}
                data={data}
                showDetail={!isMediaMin800}
                showSearchBtn={showSearchBtn}
                onSearch={onSearch}
              >
                {isMobile ? (
                  <div className="flex-1 h-[130px] flex-shrink-0">
                    <div className="flex flex-col h-full justify-end items-end text-right">
                      {needRouteBtn && (
                        <IconButton onClick={handleOpenRouteDialog}>
                          <AutoAwesomeRoundedIcon color="primary" />
                        </IconButton>
                      )}

                      <InfoBtn onClick={handleOpenOptionDialog} isExpanded={isExpanded} />
                      <ExpandBtn onClick={handleExpandChange} isExpanded={isExpanded} />
                    </div>
                  </div>
                ) : null}
              </SummaryInfo>
            </Grid>

            {!isMediaMin800 ? null : (
              <Grid item xs={12} sm={isMediaMax900 && isMediaMin800 ? 3 : 2.5}>
                <SummaryAirline
                  showLastSeen={showLastSeen}
                  data={data}
                  showSearchBtn={showSearchBtn}
                  onSearch={onSearch}
                />
              </Grid>
            )}

            <Grid item xs={12} sm className={cn("flex items-center", { "justify-end": !isMobile })}>
              <div onClick={handleOpenOptionDialog}>
                <SummaryOptions key={`${data.hash}`} data={data} partialDisplay={partialDisplay} />
              </div>
            </Grid>

            {isMobile ? null : (
              <div>
                {needRouteBtn && (
                  <Grid item className="!pl-[0px] !ml-[10px] !pt-[12px]" sm={0.5}>
                    <IconButton onClick={handleOpenRouteDialog}>
                      <AutoAwesomeRoundedIcon color="primary" />
                    </IconButton>
                  </Grid>
                )}
                <Grid item className="!pl-[0px] !ml-[10px]" sm={0.5}>
                  <InfoBtn onClick={handleOpenOptionDialog} isExpanded={isExpanded} />
                </Grid>
                <Grid item className="!pl-[0px] !ml-[10px]" sm={0.5}>
                  <ExpandBtn onClick={handleExpandChange} isExpanded={isExpanded} />
                </Grid>
              </div>
            )}
          </Grid>
        </div>
      </AccordionSummary>
      <div>

      </div>
      <AccordionDetails
        className="!pt-[15px] !pb-[20px]"
        sx={{ borderTop: (theme) => `1px solid ${theme.palette.divider}` }}
      >
        {products.map((product, i) => (
          <ProductInfo key={i} data={data} product={product} isLast={i === products.length - 1} />
        ))}
      </AccordionDetails>
    </Accordion>
  );
};

export default React.memo(FlightItem);
